import * as React from 'react';
import { lazy, Suspense } from 'react';
import Breadcrumb from 'src/react/components/breadcrumbs/containers/Breadcrumb';
import app from 'srcJs/App';
import { PAYMENT_HISTORY_URL } from 'modulePaymentIndexHistory/constants';
import { USER_PROFILE_MAIN_URL } from 'moduleUserMain/constants';
import { USER_PROFILE_CHANGE_PASSWORD_URL } from 'moduleUserChangePassword/constants';
import { USER_PROFILE_REFERRAL_URL } from 'moduleUserReferral/constants';
import { USER_PROFILE_REVIEW_URL } from 'moduleUserReview/constants';
import { USER_PROFILE_DEALS_SETTINGS_URL } from 'moduleDealsSettings/constants';
import { Switch } from 'components/router/Switch';
import { Route } from 'components/router/Route';
import { Redirect } from 'components/router/Redirect';

import {
  USER_PROFILE_API_PAGE_URL,
  USER_PROFILE_NOTIFICATION,
} from 'moduleUser/modules/api-page/constants';
import { USER_PUBLIC_PROFILE_URL } from 'modules/user/modules/public-profile/constants';
import { PAYMENT_INVOICES_URL } from 'modulePaymentIndexInvoices/constants';

const ChannelModule = lazy(
  () => import('modules/channel/modules/index/containers/ChannelModule'),
);
const ProjectModule = lazy(
  () => import('modules/project/modules/index/containers/ProjectModule'),
);
const CatalogChannelList = lazy(
  () => import('moduleCatalogChannelList/containers/List'),
);
const MyListsList = lazy(
  () => import('modules/lists/modules/list/containers/MyListsList'),
);
const MyListView = lazy(
  () => import('moduleLists/modules/view/containers/MyListView'),
);

const Chat = lazy(() => import('moduleChatIndex/containers/Chat'));
const Cart = lazy(() => import('moduleCart/containers/Cart'));
const CartComplete = lazy(
  () => import('moduleCartComplete/containers/CartComplete'),
);
const Profile = lazy(() => import('moduleUserProfile/containers/Profile'));
const PaymentIndex = lazy(() => import('modulePaymentIndex/containers/Index'));
const ChannelCreateReview = lazy(
  () => import('modules/review/modules/channel/modules/add/containers/ChannelCreateReview'),
);
const CustomerCreateReview = lazy(
  () => import('moduleReviewCustomer/modules/add/containers/CustomerCreateReview'),
);
const ReviewCreateOnReject = lazy(
  () => import('moduleReviewCreateOnReject/containers/ReviewCreateOnReject'),
);
const PublicProfile = lazy(
  () => import('moduleUserPublicProfile/containers/PublicProfile'),
);

const AppRoutes = () => (
  <Switch>
    <Route
      path="/"
      render={() => (
        <>
          <Breadcrumb title={app.tForReact('base', 'Main')} url="/" />
          <Switch>
            <Route
              path="/"
              exact
              render={() => {
                const user = app.getUser().getData();
                if (user.isCustomer) {
                  return <Redirect to="/project/list" />;
                }

                if (user.isOwner) {
                  return <Redirect to="/channel/list" />;
                }
                window.location.href = '/';
                return null;
              }}
            />
            <Route
              path={[
                PAYMENT_HISTORY_URL,
                PAYMENT_INVOICES_URL,
              ]}
              render={() => (
                <Suspense fallback="">
                  <PaymentIndex />
                </Suspense>
              )}
            />
            <Route
              path={[
                USER_PROFILE_MAIN_URL,
                USER_PROFILE_CHANGE_PASSWORD_URL,
                USER_PROFILE_REFERRAL_URL,
                USER_PROFILE_REVIEW_URL,
                USER_PROFILE_DEALS_SETTINGS_URL,
                USER_PROFILE_API_PAGE_URL,
                USER_PROFILE_NOTIFICATION,
              ]}
              render={() => (
                <Suspense fallback="">
                  <Profile />
                </Suspense>
              )}
            />
            <Route
              path={USER_PUBLIC_PROFILE_URL}
              render={({ match }) => (
                <Suspense fallback="">
                  <PublicProfile
                    id={Number(match.params.id)}
                    type={match.params.type}
                  />
                </Suspense>
              )}
            />
            <Route
              path="/cart"
              exact
              render={() => (
                <Suspense fallback="">
                  <Cart />
                </Suspense>
              )}
            />
            <Route
              path="/cart/complete"
              render={() => (
                <Suspense fallback="">
                  <CartComplete />
                </Suspense>
              )}
            />
            <Route
              path="/chat/:id?"
              render={({ match }) => {
                const { id } = match.params;
                return (
                  <Suspense fallback="">
                    <Chat id={id ? Number(id) : null} />
                  </Suspense>
                );
              }}
            />
            <Route
              path="/lists/recommended"
              render={() => (
                <Suspense fallback="">
                  <MyListsList isRecommended />
                </Suspense>
              )}
            />
            <Route
              path="/lists"
              render={() => (
                <Suspense fallback="">
                  <MyListsList />
                </Suspense>
              )}
            />
            <Route
              path="/list/view/:id"
              render={({ match }) => {
                const { id } = match.params;
                return (
                  <Suspense fallback="">
                    <MyListView id={id} key={id} path={match.path} />
                  </Suspense>
                );
              }}
            />
            <Route
              path="/catalog/channel/list"
              render={({ match }) => (
                <Suspense fallback="">
                  <CatalogChannelList path={match.path} />
                </Suspense>
              )}
            />
            <Route
              path="/project"
              render={() => (
                <Suspense fallback="">
                  <ProjectModule />
                </Suspense>
              )}
            />
            <Route
              path="/channel"
              render={() => (
                <Suspense fallback="">
                  <ChannelModule />
                </Suspense>
              )}
            />
            <Route
              path="/review/channel/:channelId/deal/:dealId/add"
              render={({ match }) => {
                const { channelId, dealId } = match.params;
                return (
                  <Suspense fallback="">
                    <ChannelCreateReview
                      channelId={Number(channelId)}
                      dealId={Number(dealId)}
                    />
                  </Suspense>
                );
              }}
            />
            <Route
              path="/review/customer/:customerId/deal/:dealId/add"
              render={({ match }) => {
                const { customerId, dealId } = match.params;
                return (
                  <Suspense fallback="">
                    <CustomerCreateReview
                      customerId={Number(customerId)}
                      dealId={Number(dealId)}
                    />
                  </Suspense>
                );
              }}
            />
            <Route
              path="/review/channel/create-on-reject/:dealId"
              render={({ match }) => {
                const { dealId } = match.params;
                return (
                  <Suspense fallback="">
                    <ReviewCreateOnReject dealId={Number(dealId)} />
                  </Suspense>
                );
              }}
            />
            <Route
              path="/ui"
              render={() => {
                const Ui = lazy(() => import('modules/templates/ui/Ui'));
                return (
                  <Suspense fallback="">
                    <Ui />
                  </Suspense>
                );
              }}
            />
            <Route
              render={() => (
                <>
                  <Breadcrumb title="404" />
                  404
                </>
              )}
            />
          </Switch>
        </>
      )}
    />
  </Switch>
);

export default AppRoutes;
